import React from 'react';
import { Input, Textarea, Select } from '../FormField';
import LineItemsEditor, { LineItem } from '../ui/LineItemsEditor';
import { formatMoney, currencySymbol } from '../../utils/currency';
import { formatAmount } from '../../utils/lineItemDraft';
import type { useFieldErrors } from '../forms/_shared';

export interface DocumentFormValues {
  /** The project the record belongs to; '' until chosen. */
  projectId: string;
  clientId: string;
  taxRate: string;
  date: string;
  notes: string;
}

interface DocumentFormBodyProps {
  values: DocumentFormValues;
  onChange: (next: DocumentFormValues) => void;
  lineItems: LineItem[];
  onLineItemsChange: (items: LineItem[]) => void;
  clientOptions: { value: string; label: string }[];
  /** Projects to choose from; the project picker is hidden when omitted. */
  projectOptions?: { value: string; label: string }[];
  /** An invoice must belong to a project; a quote may not yet. */
  projectRequired?: boolean;
  /** The project cannot change once the record exists. */
  projectLocked?: boolean;
  /** "Due Date" on an invoice, "Valid Until" on a quote. */
  dateLabel: string;
  /** Currency the record is in; undefined for a brand new record. */
  currency?: string | null;
  totals: { subtotal: number; taxAmount: number; total: number };
  taxRateNum: number;
  /** The client cannot change once the record exists. */
  clientLocked: boolean;
  /** Everything is read-only once the record has left draft. */
  readOnly: boolean;
  /** Server-side field errors (a 422) to show under the matching fields. */
  fieldErrors?: ReturnType<typeof useFieldErrors>;
}

/**
 * The fields shared by the client quote and client invoice pages. The line
 * items use the shared LineItemsEditor directly on the page, not inside a
 * dialog.
 */
export const DocumentFormBody: React.FC<DocumentFormBodyProps> = ({
  values, onChange, lineItems, onLineItemsChange, clientOptions, projectOptions, projectRequired = false, projectLocked = false, dateLabel,
  currency, totals, taxRateNum, clientLocked, readOnly, fieldErrors,
}) => {
  const set = (patch: Partial<DocumentFormValues>, ...cleared: string[]) => {
    cleared.forEach(field => fieldErrors?.clear(field));
    onChange({ ...values, ...patch });
  };
  // The date is `valid_until` on a quote and `due_date` on an invoice.
  const dateError = fieldErrors?.errorFor('valid_until') ?? fieldErrors?.errorFor('due_date');
  const money = (n: number) => formatAmount(n, currency, formatMoney);
  return (
    <>
      {projectOptions && (
        <Select
          label="Project"
          required={projectRequired}
          value={values.projectId}
          error={fieldErrors?.errorFor('project_id')}
          onChange={e => set({ projectId: e.target.value }, 'project_id')}
          placeholder="Select a project"
          options={projectOptions}
          disabled={projectLocked || readOnly}
        />
      )}
      <Select
        label="Client"
        required
        value={values.clientId}
        error={fieldErrors?.errorFor('client_id')}
        onChange={e => set({ clientId: e.target.value }, 'client_id')}
        placeholder="Select a client"
        options={clientOptions}
        disabled={clientLocked || readOnly}
      />

      <fieldset className="ap-fieldset" disabled={readOnly}>
        {fieldErrors?.errorFor('items') && <span className="form-error-text" role="alert">{fieldErrors.errorFor('items')}</span>}
        <LineItemsEditor
          items={lineItems}
          onChange={onLineItemsChange}
          addLabel="Add line item"
          // No symbol until the record has a currency: the editor then uses
          // the project currency, or says "currency not set".
          currencySymbol={currency ? currencySymbol(currency) : ''}
          fields={[
            { key: 'description', type: 'text', header: 'Description', placeholder: 'Description' },
            { key: 'quantity', type: 'number', header: 'Qty', placeholder: 'Qty', min: '0', step: '1', width: 80 },
            { key: 'unit_price', type: 'currency', header: 'Unit Price', placeholder: '0' },
          ]}
          total={{ compute: v => (Number(v.quantity) || 0) * (Number(v.unit_price) || 0) }}
          section={{ title: 'Line Items' }}
        />
      </fieldset>

      <div className="ap-field-row">
        <Input
          label="Tax Rate (%)"
          type="number"
          min={0}
          step="0.1"
          value={values.taxRate}
          error={fieldErrors?.errorFor('tax_rate')}
          onChange={e => set({ taxRate: e.target.value }, 'tax_rate')}
          disabled={readOnly}
        />
        <Input
          label={dateLabel}
          type="date"
          value={values.date}
          error={dateError}
          onChange={e => set({ date: e.target.value }, 'valid_until', 'due_date')}
          disabled={readOnly}
        />
      </div>

      <div className="ap-summary" aria-live="polite">
        <span>Subtotal: {money(totals.subtotal)}</span>
        <span>Tax ({taxRateNum}%): {money(totals.taxAmount)}</span>
        <span className="ap-summary-total">Total: {money(totals.total)}</span>
      </div>

      <Textarea
        label="Notes"
        value={values.notes}
        error={fieldErrors?.errorFor('notes')}
        onChange={e => set({ notes: e.target.value }, 'notes')}
        rows={3}
        disabled={readOnly}
      />
    </>
  );
};

export default DocumentFormBody;
